import type {CSSProperties} from 'react';
import {WEDGE_EMAIL_COMPOSER as C} from '@territory/tokens';
import {wedgeExactRole as r} from './wedgeRoles.js';

export type WedgeEmailComposerLook = 'emailc-cancel' | 'emailc-send-gmail' | 'emailc-send-outlook' | 'emailc-add'
  | 'emailc-tone' | 'emailc-tone-on' | 'emailc-regen' | 'emailc-reconnect' | 'emailc-back' | 'emailc-send-now'
  | 'emailc-client' | 'emailc-client-gmail-on' | 'emailc-client-outlook-on'
  | 'emailc-cta-gmail' | 'emailc-cta-outlook' | 'emailc-open-gmail' | 'emailc-open-outlook' | 'emailc-draft';
export const isWedgeEmailComposerLook = (look: string): look is WedgeEmailComposerLook => look.startsWith('emailc-');

/* The email composer top bar and no-contact prompt. Cancel is a bare accent text button. The send mark is a 40px circle: a brand
   gradient with a pulse ring and glow per mail client while a draft is ready, the elevated fill with the faint ink while it is not
   (the ring and glow variables stay declared, as shipped, because the caller adds the pulse class only when ready). The add button
   is a 48px accent action that dims while the address saves. None draws a transparent target: the send mark and Cancel sit in a
   bar with 16px of padding, and the add button is already 48px tall. */
export function wedgeEmailComposerStyle(look: WedgeEmailComposerLook, disabled: boolean): CSSProperties {
  if (look === 'emailc-cancel') return {background: 'none', border: 0, color: r('accent'), fontSize: 16, fontWeight: 500, cursor: 'pointer', padding: 6};
  if (look === 'emailc-add') return {
    minHeight: 48, padding: '0 18px', borderRadius: 12, border: 0, cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? r('bgElev') : r('accent'), color: disabled ? r('labelQuad') : C.ink,
    fontSize: 15, fontWeight: 700, fontFamily: r('font'), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };
  if (look === 'emailc-tone' || look === 'emailc-tone-on') {
    const on = look === 'emailc-tone-on';
    return {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999,
      background: on ? r('accent') : r('bgElev'), color: on ? C.ink : r('labelSec'),
      border: on ? `1px solid ${r('accent')}` : `1px solid ${r('separator')}`, cursor: 'pointer',
      fontSize: 12.5, fontWeight: 600, fontFamily: r('font'), whiteSpace: 'nowrap', flexShrink: 0,
    };
  }
  if (look === 'emailc-regen') return {
    background: C.regenFill, border: `1px solid ${C.regenBorder}`, borderRadius: 999,
    padding: '0 16px 0 6px', minHeight: 40, color: C.ink, fontSize: 14, fontWeight: 700, fontFamily: r('font'), cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, boxShadow: C.regenShadow,
  };
  if (look === 'emailc-reconnect') return {
    alignSelf: 'flex-start', minHeight: 40, padding: '0 16px', borderRadius: 12, border: 0, cursor: 'pointer',
    background: r('accent'), color: C.ink, fontSize: 14, fontWeight: 700, fontFamily: r('font'), display: 'inline-flex', alignItems: 'center', gap: 8,
  };
  if (look === 'emailc-back') return {
    minHeight: 54, padding: '0 22px', borderRadius: 16, border: `1px solid ${r('separator')}`, background: r('bgElev'), color: r('label'),
    fontSize: 15, fontWeight: 700, fontFamily: r('font'), cursor: disabled ? 'not-allowed' : 'pointer',
  };
  if (look === 'emailc-send-now') return {
    flex: '1 1 200px', minHeight: 54, padding: '0 22px', borderRadius: 16, border: 0,
    cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    background: disabled ? r('bgElev') : C.regenFill, color: disabled ? r('labelQuad') : C.ink, fontFamily: r('font'), fontSize: 16.5, fontWeight: 800,
    ['--rv-mail-ring' as string]: C.greenRing, ['--rv-mail-glow' as string]: C.greenGlow,
  };
  if (look === 'emailc-client' || look === 'emailc-client-gmail-on' || look === 'emailc-client-outlook-on') {
    const on = look !== 'emailc-client';
    const ring = look === 'emailc-client-outlook-on' ? C.outlookDotRing : C.gmailDotRing;
    return {
      display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 14px', borderRadius: 12, border: 0, cursor: 'pointer',
      background: on ? r('surface') : 'transparent', color: on ? r('label') : r('labelSec'),
      boxShadow: on ? `0 0 0 2px ${ring}, ${C.clientShadow}` : 'none',
      fontSize: 14, fontWeight: 700, fontFamily: r('font'),
    };
  }
  if (look === 'emailc-cta-gmail' || look === 'emailc-cta-outlook' || look === 'emailc-open-gmail' || look === 'emailc-open-outlook') {
    const outlook = look.endsWith('outlook');
    return {
      ...(look.startsWith('emailc-open') ? {flex: '1 1 220px'} : {}),
      minHeight: 54, padding: '0 22px', borderRadius: 16, border: 0,
      cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
      background: disabled ? r('bgElev') : (outlook ? C.ctaOutlookFill : C.ctaGmailFill),
      color: disabled ? r('labelQuad') : C.ink, fontFamily: r('font'),
      ['--rv-mail-ring' as string]: outlook ? C.ctaOutlookRing : C.ctaGmailRing,
      ['--rv-mail-glow' as string]: outlook ? C.ctaOutlookGlow : C.ctaGmailGlow,
    };
  }
  if (look === 'emailc-draft') return {
    minHeight: 44, padding: '0 16px', borderRadius: 12, border: `1px solid ${r('separator')}`, background: 'transparent',
    color: disabled ? r('labelQuad') : r('labelSec'), fontFamily: r('font'), fontSize: 13.5, fontWeight: 650,
    cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };
  const outlook = look === 'emailc-send-outlook';
  return {
    width: 40, height: 40, borderRadius: '50%',
    background: disabled ? r('bgElev') : (outlook ? C.outlookFill : C.gmailFill),
    ['--rv-mail-ring' as string]: outlook ? C.outlookRing : C.gmailRing,
    ['--rv-mail-glow' as string]: outlook ? C.outlookGlow : C.gmailGlow,
    color: disabled ? r('labelQuad') : C.ink,
    border: 0, padding: 0, cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: `all 200ms ${r('ease')}`,
  };
}
